import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, LayoutDashboard, FolderOpen, UserCheck, Calendar, GitBranch,
  BarChart3, Plus, X, RefreshCw,
} from 'lucide-react';
import DraggableTabBar from '../components/DraggableTabBar';

// ─── Tab Components ──────────────────────────────────────────────────────────
import DashboardTab from './personnel/DashboardTab';
import DirectoryTab from './personnel/DirectoryTab';
import ProfileModal from './personnel/ProfileModal';
import LeaveTab from './personnel/LeaveTab';
import OrgChartTab from './personnel/OrgChartTab';
import ReportsTab from './personnel/ReportsTab';

// ─── Shared Data ─────────────────────────────────────────────────────────────
import { generateDemoPersonnel } from './personnel/personnelShared';

// ─── Neumorphic Design Tokens ────────────────────────────────────────────────
const neuShadow = '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)';
const neuShadowSm = '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.8)';
const neuShadowInset = 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)';
const neuShadowPressed = 'inset 4px 4px 8px rgba(186,199,216,0.45), inset -4px -4px 8px rgba(255,255,255,0.8)';

const btnPrimary = {
  padding: '8px 16px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(145deg, #3CA0AE, #256F7A)', color: '#fff',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 5,
  boxShadow: `${neuShadow}, inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.1)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)', position: 'relative',
};

const btnSm = {
  padding: '6px 12px', borderRadius: 8, border: 'none',
  background: '#F8F9FA', color: '#6C757D',
  cursor: 'pointer', fontSize: 12, fontWeight: 500,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  boxShadow: `${neuShadowSm}, inset 0 1px 0 rgba(255,255,255,0.35)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)', position: 'relative',
};

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#F8F9FA', borderRadius: 20, width: 620, maxHeight: '85vh', overflow: 'auto', padding: 28, boxShadow: '12px 12px 24px rgba(186,199,216,0.65), -12px -12px 24px rgba(255,255,255,0.9)' };
const closeBtn = { background: '#F8F9FA', border: 'none', cursor: 'pointer', borderRadius: 8, padding: 4, boxShadow: neuShadowSm };
const inp = { padding: '8px 12px', borderRadius: 10, border: 'none', fontSize: 13, width: '100%', background: '#F8F9FA', boxShadow: neuShadowInset, outline: 'none', color: '#212529', transition: 'box-shadow 0.15s ease' };

// ─── Tab Definitions ─────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'directory', label: 'Directory', icon: Users },
  { id: 'leave',     label: 'Leave & Time Off', icon: Calendar },
  { id: 'orgchart',  label: 'Org Chart', icon: GitBranch },
  { id: 'reports',   label: 'Reports', icon: BarChart3 },
];

// ─── Job Category Constants ──────────────────────────────────────────────────
const JOB_CATEGORIES = {
  'Clinical Leadership': { color: '#2E8B98', titles: ['Medical Director', 'Chief Medical Officer', 'Department Head', 'Clinical Supervisor'] },
  'Physicians': { color: '#256F7A', titles: ['Attending Physician', 'Resident Physician', 'Fellow', 'Consulting Physician', 'Specialist'] },
  'Nursing': { color: '#62BB46', titles: ['Registered Nurse', 'Nurse Practitioner', 'Licensed Practical Nurse', 'Charge Nurse', 'Nurse Manager'] },
  'Allied Health': { color: '#4FA038', titles: ['Medical Assistant', 'Phlebotomist', 'Radiology Technologist', 'Respiratory Therapist', 'Physical Therapist'] },
  'Research': { color: '#F26522', titles: ['Principal Investigator', 'Research Coordinator', 'Lab Technician', 'Data Analyst', 'Biostatistician'] },
  'Administration': { color: '#DC3545', titles: ['Practice Administrator', 'Office Manager', 'HR Coordinator', 'Compliance Officer', 'Quality Manager'] },
  'IT & Engineering': { color: '#6C757D', titles: ['Systems Administrator', 'Network Engineer', 'Software Developer', 'Database Administrator', 'Security Analyst'] },
  'Billing & Finance': { color: '#256F7A', titles: ['Billing Specialist', 'Revenue Cycle Manager', 'Coding Specialist', 'Financial Analyst', 'Collections Coordinator'] },
  'Front Office': { color: '#2E8B98', titles: ['Receptionist', 'Patient Access Rep', 'Scheduling Coordinator', 'Medical Records Clerk', 'Registration Specialist'] },
};

const DEPARTMENTS = ['Cardiology', 'Dermatology', 'Emergency', 'Family Medicine', 'Internal Medicine',
  'Neurology', 'Oncology', 'Orthopedics', 'Pediatrics', 'Psychiatry', 'Radiology', 'Surgery',
  'Laboratory', 'Pharmacy', 'Administration', 'Information Technology', 'Human Resources', 'Finance'];

const STATUS_OPTIONS = ['active', 'inactive', 'on_leave', 'terminated'];

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '', job_title: '', department: '',
  hire_date: '', status: 'active', supervisor: '', employee_id: '', notes: '',
  birth_date: '', location: '', employment_type: 'full_time',
};

// Build a full personnel record with all naming variants for local/demo use
function buildFullRecord(form) {
  const id = Date.now();
  const firstName = form.first_name || '';
  const lastName = form.last_name || '';
  const jobTitle = form.job_title || '';
  // Find job category from JOB_CATEGORIES
  let jobCategory = 'Administration';
  for (const [cat, { titles }] of Object.entries(JOB_CATEGORIES)) {
    if (titles.includes(jobTitle)) { jobCategory = cat; break; }
  }
  return {
    id,
    // snake_case
    first_name: firstName, last_name: lastName, job_title: jobTitle,
    job_category: jobCategory, hire_date: form.hire_date || new Date().toISOString().slice(0, 10),
    date_of_birth: form.birth_date || '', birth_date: form.birth_date || '',
    employee_id: form.employee_id || `EMP-${id}`, employment_type: form.employment_type || 'full_time',
    // camelCase
    firstName, lastName, jobTitle, jobCategory,
    hireDate: form.hire_date || new Date().toISOString().slice(0, 10),
    birthDate: form.birth_date || '', employmentType: form.employment_type || 'full_time',
    // composite
    name: `${firstName} ${lastName}`.trim(),
    // shared fields
    department: form.department || '', location: form.location || '', status: form.status || 'active',
    phone: form.phone || '', email: form.email || '', salary: 0, supervisor: form.supervisor || '',
    notes: form.notes || '', created_at: new Date().toISOString(),
  };
}

// ─── Add / Edit Modal ────────────────────────────────────────────────────────
function PersonnelModal({ person, onClose, onSave }) {
  const [form, setForm] = useState(person ? { ...person } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.job_title) {
      alert('First name, last name, and job title are required.');
      return;
    }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (err) { alert('Error: ' + (err.message || 'Failed to save')); }
    finally { setSaving(false); }
  }

  const fieldRow = { display: 'flex', gap: 12, marginBottom: 14 };
  const fieldCol = { flex: 1 };
  const lbl = { fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>
            {person ? 'Edit Personnel' : 'Add Personnel'}
          </h3>
          <button style={closeBtn} onClick={onClose}><X size={16} color="#6C757D" /></button>
        </div>

        <form onSubmit={submit}>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <label style={lbl}>First Name *</label>
              <input style={inp} value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First name" />
            </div>
            <div style={fieldCol}>
              <label style={lbl}>Last Name *</label>
              <input style={inp} value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Last name" />
            </div>
          </div>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <label style={lbl}>Employee ID</label>
              <input style={inp} value={form.employee_id} onChange={e => set('employee_id', e.target.value)} placeholder="EMP-001" />
            </div>
            <div style={fieldCol}>
              <label style={lbl}>Job Title *</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.job_title} onChange={e => set('job_title', e.target.value)}>
                <option value="">Select job title...</option>
                {Object.entries(JOB_CATEGORIES).map(([cat, { titles }]) => (
                  <optgroup key={cat} label={cat}>
                    {titles.map(t => <option key={t} value={t}>{t}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <label style={lbl}>Department</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.department} onChange={e => set('department', e.target.value)}>
                <option value="">Select department...</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div style={fieldCol}>
              <label style={lbl}>Status</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
              </select>
            </div>
          </div>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <label style={lbl}>Email</label>
              <input style={inp} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="name@zignagenix.com" />
            </div>
            <div style={fieldCol}>
              <label style={lbl}>Phone</label>
              <input style={inp} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" />
            </div>
          </div>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <label style={lbl}>Hire Date</label>
              <input style={inp} type="date" value={form.hire_date} onChange={e => set('hire_date', e.target.value)} />
            </div>
            <div style={fieldCol}>
              <label style={lbl}>Date of Birth</label>
              <input style={inp} type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)} />
            </div>
          </div>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <label style={lbl}>Location</label>
              <input style={inp} value={form.location} onChange={e => set('location', e.target.value)} placeholder="Office location" />
            </div>
            <div style={fieldCol}>
              <label style={lbl}>Employment Type</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.employment_type} onChange={e => set('employment_type', e.target.value)}>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="per_diem">Per Diem</option>
              </select>
            </div>
          </div>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <label style={lbl}>Supervisor</label>
              <input style={inp} value={form.supervisor} onChange={e => set('supervisor', e.target.value)} placeholder="Supervisor name" />
            </div>
            <div style={fieldCol} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, height: 60, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Additional notes..." />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button type="button" style={btnSm} onClick={onClose}>Cancel</button>
            <button type="submit" style={btnPrimary} disabled={saving}>
              {saving ? 'Saving...' : person ? 'Update' : 'Add Personnel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Personnel Management Page ──────────────────────────────────────────
export default function PersonnelManagementPage({ user }) {
  const [personnel, setPersonnel] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showModal, setShowModal] = useState(false);
  const [editPerson, setEditPerson] = useState(null);
  const [profilePerson, setProfilePerson] = useState(null);

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadPersonnel = useCallback(async () => {
    try {
      const result = await window.zignagenixAPI.getPersonnel();
      if (result?.error) throw new Error(result.message);
      const data = Array.isArray(result) ? result : (result?.data || []);
      setPersonnel(data.length > 0 ? data : generateDemoPersonnel(50));
    } catch {
      setPersonnel(generateDemoPersonnel(50));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeaveRequests = useCallback(async () => {
    try {
      const result = await window.zignagenixAPI.getLeaveRequests();
      if (result?.error) throw new Error(result.message);
      setLeaveRequests(Array.isArray(result) ? result : (result?.data || []));
    } catch {
      setLeaveRequests([]);
    }
  }, []);

  useEffect(() => { loadPersonnel(); loadLeaveRequests(); }, [loadPersonnel, loadLeaveRequests]);

  // ── CRUD handlers ─────────────────────────────────────────────────────────
  async function handleSave(form) {
    try {
      if (editPerson?.id) {
        const result = await window.zignagenixAPI.updatePersonnel(editPerson.id, form);
        if (result?.error) throw new Error(result.message);
        loadPersonnel();
      } else {
        const result = await window.zignagenixAPI.createPersonnel(form);
        if (result?.error) throw new Error(result.message);
        loadPersonnel();
      }
    } catch (err) {
      console.warn('[Personnel] DB save failed, using local state:', err?.message || err);
      if (editPerson?.id) {
        setPersonnel(prev => prev.map(p => p.id === editPerson.id ? { ...p, ...form, name: `${form.first_name} ${form.last_name}`.trim() } : p));
      } else {
        const newRecord = buildFullRecord(form);
        setPersonnel(prev => [...prev, newRecord]);
      }
    }
  }

  async function handleDeactivate(id) {
    if (!window.confirm('Deactivate this personnel record?')) return;
    try {
      await window.zignagenixAPI.deactivatePersonnel(id);
      loadPersonnel();
    } catch {
      setPersonnel(prev => prev.map(p => p.id === id ? { ...p, status: 'terminated' } : p));
    }
  }

  function handleEdit(person) {
    setEditPerson(person);
    setShowModal(true);
  }

  function handleAdd() {
    setEditPerson(null);
    setShowModal(true);
  }

  function handleViewProfile(person) {
    setProfilePerson(person);
  }

  function handleNavigateTab(tabId) {
    if (tabId === 'add-employee') {
      handleAdd();
    } else {
      setActiveTab(tabId);
    }
  }

  // ── Tab style function ────────────────────────────────────────────────────
  const tabStyle = (tabId) => ({
    padding: '8px 16px', borderRadius: 10, border: 'none',
    background: activeTab === tabId ? 'linear-gradient(145deg, #3CA0AE, #256F7A)' : '#F8F9FA',
    color: activeTab === tabId ? '#fff' : '#6C757D',
    cursor: 'pointer', fontSize: 12, fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: 6,
    boxShadow: activeTab === tabId ? neuShadowPressed : neuShadowSm,
    transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)',
    marginRight: 4,
  });

  // ── Render active tab content ─────────────────────────────────────────────
  function renderTab() {
    if (loading) {
      return <div style={{ padding: 60, textAlign: 'center', color: '#ADB5BD', fontSize: 14 }}>Loading personnel data...</div>;
    }

    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab personnel={personnel} leaveRequests={leaveRequests} onNavigate={handleNavigateTab} />;
      case 'directory':
        return (
          <DirectoryTab
            personnel={personnel}
            onEdit={handleEdit}
            onDeactivate={handleDeactivate}
            onViewProfile={handleViewProfile}
            onAdd={handleAdd}
            user={user}
          />
        );
      case 'leave':
        return <LeaveTab personnel={personnel} user={user} />;
      case 'orgchart':
        return <OrgChartTab personnel={personnel} />;
      case 'reports':
        return <ReportsTab personnel={personnel} />;
      default:
        return <DashboardTab personnel={personnel} leaveRequests={leaveRequests} onNavigate={handleNavigateTab} />;
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F0F2F5' }}>
      {/* Header */}
      <div style={{ padding: '20px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1A2332', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users size={24} color="#2E8B98" /> Personnel Management
          </h1>
          <p style={{ fontSize: 12, color: '#6C757D', margin: '4px 0 0 34px' }}>
            Staff directory, leave management, org structure, and workforce analytics
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btnSm} onClick={loadPersonnel} title="Refresh"><RefreshCw size={14} /></button>
          <button style={btnPrimary} onClick={handleAdd}><Plus size={14} /> Add Employee</button>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ padding: '16px 28px 0' }}>
        <DraggableTabBar
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          prefKey="personnel_tab_order"
          style={{
            display: 'flex', gap: 4, padding: '6px 8px',
            background: '#F8F9FA', borderRadius: 14,
            boxShadow: neuShadow,
          }}
          tabStyle={tabStyle}
          iconSize={14}
        />
      </div>

      {/* Tab Content */}
      <div style={{ padding: '16px 28px 28px' }}>
        {renderTab()}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <PersonnelModal
          person={editPerson}
          onClose={() => { setShowModal(false); setEditPerson(null); }}
          onSave={handleSave}
        />
      )}

      {/* Profile Slide-Out */}
      {profilePerson && (
        <ProfileModal
          person={profilePerson}
          onClose={() => setProfilePerson(null)}
          onEdit={(p) => { setProfilePerson(null); handleEdit(p); }}
          onSave={handleSave}
          allPersonnel={personnel}
        />
      )}
    </div>
  );
}
